/**
 * Числовая ячейка (§5.2, тип «Число»): выравнивание справа, tnum, проверка
 * `min`/`max` из конфига поля. Нечисловой ввод не принимается, причина —
 * строкой под ячейкой (§12.7).
 */
import { useEffect, useRef, useState } from 'react'
import { Input } from '../../../../components/index.js'
import { tableTexts as t } from '../../texts.table.js'
import type { CellProps } from './CellBox.js'

export function NumberCell(props: CellProps) {
  const { column, cell, editing, readOnly, seed, write, stepAfterCommit, commitAndRight, closeEditor } = props
  const published = typeof cell.value === 'number' ? String(cell.value) : ''
  const [value, setValue] = useState(seed ?? published)
  const latest = useRef(value)

  const min = column.field?.config.min
  const max = column.field?.config.max

  useEffect(() => {
    if (!editing) return
    setValue(seed ?? published)
    latest.current = seed ?? published
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  if (!editing || readOnly) {
    if (cell.empty) return <span className="cell__empty">{t.empty.dash}</span>
    return <span className="cell__value">{published}</span>
  }

  const parse = (raw: string): number | null | undefined => {
    const trimmed = raw.trim().replace(',', '.')
    if (trimmed === '') return null
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed)) return undefined
    if (min !== undefined && parsed < min) return undefined
    if (max !== undefined && parsed > max) return undefined
    return parsed
  }

  const parsed = parse(latest.current)
  const error = parsed === undefined
    ? (min !== undefined && max !== undefined ? `Значение должно быть от ${min} до ${max}` : 'Нужно число')
    : undefined

  const flush = () => {
    const next = parse(latest.current)
    if (next === undefined) return
    if (next !== (typeof cell.value === 'number' ? cell.value : null)) write(next)
  }

  return (
    <div
      className="cell__editor"
      onKeyDown={(event) => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeEditor(); return }
        if (event.key === 'Enter') {
          event.preventDefault(); event.stopPropagation()
          if (parse(latest.current) === undefined) return
          flush()
          stepAfterCommit(event.shiftKey ? -1 : 1)
          return
        }
        if (event.key === 'Tab') {
          event.preventDefault(); event.stopPropagation()
          flush()
          commitAndRight()
        }
      }}
    >
      <Input
        autoFocus
        numeric
        size="cell"
        value={value}
        invalid={parsed === undefined}
        error={error}
        aria-label={column.title}
        onChange={(event) => { setValue(event.target.value); latest.current = event.target.value }}
        onBlur={flush}
      />
    </div>
  )
}
